import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { IconCheck, IconExternalLink, IconTrash, IconX } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Group,
  IconAction,
  Stack,
  Td,
  Text,
  Textarea,
  Tr,
} from "@/components/ui";
import { PERMISOS } from "@/lib/permisos";
import { useUserPermisos } from "@/lib/auth-store";
import type {
  SolicitudDeCatalogo,
  TipoDeSolicitudCatalogo,
} from "@/features/solicitudes-catalogo/services/solicitudes-catalogo.api";
import { useResolverSolicitudCatalogo } from "../hooks/use-bandeja-solicitudes-catalogo";

const COLOR_ESTADO: Record<string, string> = {
  Pendiente: "ambar",
  Atendida: "exito",
  Rechazada: "peligro",
};

/**
 * Gestor donde se da de alta el elemento pedido, por tipo · con el permiso que exige (el
 * enlace solo aparece si el responsable puede abrirlo) y el prellenado (deep-link) que acepta.
 */
const CATALOGO: Record<string, { href: string; permiso: string; search?: (s: SolicitudDeCatalogo) => object }> = {
  Proveedor: {
    href: "/admin/proveedores",
    permiso: PERMISOS.adminProveedores,
    search: (s) => ({ altaRfc: s.clave, altaRazonSocial: s.descripcion ?? undefined }),
  },
  CuentaContablePorClave: {
    href: "/admin/mapeos-clave-cuenta",
    permiso: PERMISOS.adminMapeosClaveCuenta,
    search: (s) => ({ altaClaveSat: s.clave }),
  },
  ClaveSat: { href: "/admin/catalogos-sat", permiso: PERMISOS.adminCatalogosSat },
  CentroDeCosto: { href: "/admin/catalogos-contables", permiso: PERMISOS.adminCatalogosContables },
  CuentaContable: { href: "/admin/catalogos-contables", permiso: PERMISOS.adminCatalogosContables },
  EquivalenciaUnidad: { href: "/admin/equivalencias-unidad", permiso: PERMISOS.adminEquivalenciasUnidad },
};

/** La clave de una equivalencia viaja como «SAT|SAP» · se muestra legible. */
export function claveLegible(s: Pick<SolicitudDeCatalogo, "tipo" | "clave">): string {
  return s.tipo === "EquivalenciaUnidad" ? s.clave.replace("|", " ↔ ") : s.clave;
}

export function FilaSolicitudCatalogo({
  s,
  tipos,
}: {
  s: SolicitudDeCatalogo;
  tipos: ReadonlyArray<TipoDeSolicitudCatalogo>;
}) {
  const navigate = useNavigate();
  const permisos = useUserPermisos();
  const { atender, rechazar, eliminar } = useResolverSolicitudCatalogo();
  const [modal, setModal] = useState<"atender" | "rechazar" | null>(null);
  const [comentario, setComentario] = useState("");

  const config = tipos.find((t) => t.tipo === s.tipo);
  const exigeMotivo = config?.rechazoExigeMotivo ?? true;
  const catalogo = CATALOGO[s.tipo];
  const pendiente = s.estado === "Pendiente";

  const cerrar = () => {
    setModal(null);
    setComentario("");
  };

  const confirmar = () => {
    const texto = comentario.trim() || undefined;
    if (modal === "atender") atender.mutate({ id: s.id, comentario: texto }, { onSuccess: cerrar });
    else if (modal === "rechazar") rechazar.mutate({ id: s.id, comentario: texto }, { onSuccess: cerrar });
  };

  return (
    <Tr>
      <Td>
        <Badge variant="light" size="sm">
          {config?.nombre ?? s.tipo}
        </Badge>
      </Td>
      <Td>
        <Text size="sm" fw={600} className="font-mono">
          {claveLegible(s)}
        </Text>
        {s.numeroSociedad && (
          <Text size="xs" c="dimmed">
            Sociedad {s.numeroSociedad}
          </Text>
        )}
        {s.descripcion && (
          <Text size="xs" c="dimmed" className="line-clamp-1" title={s.descripcion}>
            {s.descripcion}
          </Text>
        )}
      </Td>
      <Td>
        <Text size="sm">{s.solicitanteNombre ?? "—"}</Text>
        {s.numeroSolicitudPago && (
          <Text size="xs" c="dimmed">
            Solicitud de pago #{s.numeroSolicitudPago}
          </Text>
        )}
      </Td>
      <Td className="max-w-65">
        <Text size="sm" c={s.comentario ? undefined : "dimmed"} className="line-clamp-2" title={s.comentario ?? undefined}>
          {s.comentario ?? "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{dayjs(s.creadoAt).format("DD/MM/YYYY HH:mm")}</Text>
      </Td>
      <Td>
        <Badge variant="light" color={COLOR_ESTADO[s.estado] ?? "slate"} size="sm">
          {s.estado}
        </Badge>
        {s.atendidaAutomaticamente && (
          <Text size="xs" c="dimmed">
            Automáticamente, al darse de alta
          </Text>
        )}
        {s.respuestaComentario && (
          <Text size="xs" c="dimmed" className="line-clamp-1" title={s.respuestaComentario}>
            {s.respuestaComentario}
          </Text>
        )}
      </Td>
      <Td align="center">
        <Group gap={4} className="flex-nowrap justify-center">
          {catalogo && pendiente && permisos.includes(catalogo.permiso) && (
            <IconAction
              label="Ir al catálogo para dar de alta el elemento"
              size="sm"
              onClick={() =>
                void navigate({ to: catalogo.href as never, search: catalogo.search?.(s) as never })
              }
            >
              <IconExternalLink size={16} stroke={1.7} />
            </IconAction>
          )}
          {pendiente && (
            <>
              <IconAction label="Marcar atendida" size="sm" iconColor="exito-600" onClick={() => setModal("atender")}>
                <IconCheck size={16} stroke={1.7} />
              </IconAction>
              <IconAction label="Rechazar" size="sm" iconColor="peligro-600" onClick={() => setModal("rechazar")}>
                <IconX size={16} stroke={1.7} />
              </IconAction>
            </>
          )}
          {/* Solo las resueltas se quitan de la bandeja (el solicitante conserva su aviso). */}
          {!pendiente && (
            <IconAction
              label="Eliminar de mi bandeja"
              size="sm"
              loading={eliminar.isPending && eliminar.variables === s.id}
              onClick={() => eliminar.mutate(s.id)}
            >
              <IconTrash size={16} stroke={1.7} />
            </IconAction>
          )}
        </Group>
      </Td>

      <Dialog open={modal !== null} onOpenChange={(open) => !open && cerrar()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{modal === "atender" ? "Marcar como atendida" : "Rechazar solicitud"}</DialogTitle>
          </DialogHeader>
          <Stack gap="md">
            <Text size="sm">
              {modal === "atender" ? (
                <>
                  Confirma que <strong>{claveLegible(s)}</strong> ya está disponible en el catálogo. El solicitante
                  podrá continuar su solicitud de pago.
                </>
              ) : (
                <>
                  El solicitante verá el motivo del rechazo de <strong>{claveLegible(s)}</strong>.
                </>
              )}
            </Text>
            <Textarea
              label={modal === "atender" ? "Comentario (opcional)" : exigeMotivo ? "Motivo del rechazo" : "Motivo del rechazo (opcional)"}
              placeholder={modal === "atender" ? "p. ej. Dado de alta como proveedor 10099" : exigeMotivo ? "Obligatorio" : "Opcional"}
              value={comentario}
              onChange={(e) => setComentario(e.currentTarget.value)}
              maxLength={500}
              rows={2}
              required={modal === "rechazar" && exigeMotivo}
            />
          </Stack>
          <DialogFooter>
            <Button variant="default" onClick={cerrar}>
              Cancelar
            </Button>
            <Button
              className={modal === "rechazar" ? "bg-peligro-600 hover:bg-peligro-700" : undefined}
              disabled={modal === "rechazar" && exigeMotivo && comentario.trim() === ""}
              loading={atender.isPending || rechazar.isPending}
              onClick={confirmar}
            >
              {modal === "atender" ? "Marcar atendida" : "Rechazar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tr>
  );
}
